import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import JsBarcode from 'jsbarcode';
import { getLogo, isPdf } from '../../services/Functions';
import { pdfToImages } from '../../utils/pdfToImages';
import gepcoBillHeaderLogo from '../../images/DISCO Logos/GEPCO-bill-header.png';
import meterReadingSample from '../../images/meter-reading-sample.png';
import './MrcElectricityConsumerBill.scss';

/** Format bill amounts with thousand separators (e.g. 17669 → 17,669). */
const formatBillAmount = (value) => {
    if (value === undefined || value === null || value === '') return '';
    if (value === '-' || value === '—') return value;
    const num = Number(String(value).replace(/,/g, '').trim());
    if (!Number.isFinite(num)) return String(value);
    const hasDecimal = Math.abs(num % 1) > 0;
    return num.toLocaleString('en-US', {
        minimumFractionDigits: hasDecimal ? 2 : 0,
        maximumFractionDigits: hasDecimal ? 2 : 0,
    });
};

const LabelRow = ({ en, urdu, urduOnly = false }) => (
    <div className="label-row">
        {!urduOnly && en ? <span className="en-lbl">{en}</span> : null}
        {urdu ? <span className="ur-lbl">{urdu}</span> : null}
    </div>
);

const ValSpace = ({ children, className = '' }) => (
    <div className={`val-space ${className}`.trim()}>{children}</div>
);

const GridColCell = ({ children, divider = false, className = '' }) => (
    <div className={`grid-col-cell ${divider ? 'border-divider-left' : ''} ${className}`.trim()}>{children}</div>
);

const FieldBlock = ({ en, urdu, value, urduOnly = false, valueClassName = '' }) => (
    <>
        <LabelRow en={en} urdu={urdu} urduOnly={urduOnly} />
        <ValSpace className={valueClassName}>{value || ''}</ValSpace>
        <div style={{ height: '1px' }} />
    </>
);

const ChargesBreakdown = ({ billData }) => {
    const payableAmount = formatBillAmount(
        billData.payableWithinDueDate ??
            billData.payableAmount ??
            billData.cp52Amount ??
            billData.grandTotal ??
            ''
    );

    return (
    <div className="charges-breakdown-grid">
        <div className="charges-breakdown-col charges-breakdown-col--left">
            <div className="charges-bd-row">
                <div className="charges-bd-label">
                    <span className="charges-bd-en">Total Electricity Charges</span>
                    <span className="charges-bd-ur">کل چارجز</span>
                </div>
                <span className="charges-bd-val">
                    {formatBillAmount(billData.totalElectricityCharges)}
                </span>
            </div>
            <div className="charges-bd-row">
                <div className="charges-bd-label">
                    <span className="charges-bd-en">Subsidies</span>
                    <span className="charges-bd-ur">رعایت</span>
                </div>
                <span className="charges-bd-val">
                    {formatBillAmount(billData.subsidies)}
                </span>
            </div>
            <div className="charges-bd-row">
                <div className="charges-bd-label">
                    <span className="charges-bd-en">Net Electricity Charges</span>
                    {billData.netElectricityPercent ? (
                        <span className="charges-bd-pct">{billData.netElectricityPercent}</span>
                    ) : null}
                    <span className="charges-bd-ur">نیٹ چارجز</span>
                </div>
                <span className="charges-bd-val">
                    {formatBillAmount(billData.netElectricityCharges)}
                </span>
            </div>
            <div className="charges-bd-row">
                <div className="charges-bd-label">
                    <span className="charges-bd-en">Taxes</span>
                    {billData.taxesPercent ? (
                        <span className="charges-bd-pct">{billData.taxesPercent}</span>
                    ) : null}
                    <span className="charges-bd-ur">کل ٹیکس</span>
                </div>
                <span className="charges-bd-val">{formatBillAmount(billData.taxes)}</span>
            </div>
            <div className="charges-bd-row charges-bd-row--current">
                <div className="charges-bd-label">
                    <span className="charges-bd-en">Current Bill</span>
                    <span className="charges-bd-ur">موجودہ بل</span>
                </div>
                <span className="charges-bd-val">{payableAmount}</span>
            </div>
            <div className="charges-bd-row charges-bd-row--grand">
                <div className="charges-bd-label">
                    <span className="charges-bd-en">Grand Total</span>
                    <span className="charges-bd-ur">کل واجبات</span>
                </div>
                <span className="charges-bd-val">{payableAmount}</span>
            </div>
        </div>
        <div className="charges-breakdown-col charges-breakdown-col--right">
            <div className="charges-bd-row">
                <div className="charges-bd-label">
                    <span className="charges-bd-en charges-bd-en--strong">Arrears</span>
                    <span className="charges-bd-ur">بقایاجات</span>
                </div>
                <span className="charges-bd-val">{formatBillAmount(billData.arrears)}</span>
            </div>
            <div className="charges-bd-row">
                <div className="charges-bd-label">
                    <span className="charges-bd-en charges-bd-en--strong">Installment</span>
                    <span className="charges-bd-ur">قسط</span>
                </div>
                <span className="charges-bd-val">
                    {formatBillAmount(billData.installment)}
                </span>
            </div>
            <div className="charges-bd-row">
                <div className="charges-bd-label">
                    <span className="charges-bd-en charges-bd-en--strong">Adjustments</span>
                    <span className="charges-bd-ur">ایڈجسٹمنٹ</span>
                </div>
                <span className="charges-bd-val charges-bd-val--strong">
                    {formatBillAmount(billData.adjustments)}
                </span>
            </div>
            {billData.applicationId ? (
                <div className="charges-bd-tracking-block">
                    <div className="charges-bd-tracking-title">METER READING CORRECTION</div>
                    <div className="charges-bd-tracking-label">TRACKING ID</div>
                    <div className="charges-bd-tracking-label">{billData.applicationId}</div>
                </div>
            ) : null}
            {billData.totalFpa !== undefined && billData.totalFpa !== null && billData.totalFpa !== '' ? (
                <div className="charges-bd-row">
                    <div className="charges-bd-label">
                        <span className="charges-bd-en charges-bd-en--strong">Total FPA</span>
                    </div>
                    <span className="charges-bd-val charges-bd-val--strong">
                        {formatBillAmount(billData.totalFpa)}
                    </span>
                </div>
            ) : null}
        </div>
    </div>
    );
};

const HistoryBlockGrid = ({ rows = [], className = '', rowCount = 6, footer = null }) => {
    const paddedRows = [...rows];
    while (paddedRows.length < rowCount) {
        paddedRows.push({ month: '', status: '', units: '', bill: '', payment: '' });
    }

    return (
        <div className={`history-block-grid ${className}`.trim()} style={{ '--history-rows': rowCount }}>
            <div className="history-header-row">
                <div className="history-header-cell history-header-cell--month">MONTH</div>
                <div className="history-header-cell history-header-cell--status">STATUS</div>
                <div className="history-header-cell history-header-cell--num">UNITS</div>
                <div className="history-header-cell history-header-cell--num">BILL (RS.)</div>
                <div className="history-header-cell history-header-cell--num">PAYMENT (RS.)</div>
            </div>
            {paddedRows.slice(0, rowCount).map((row, index) => (
                <div className="history-row" key={`${row.month || 'empty'}-${index}`}>
                    <div className="history-cell history-cell--month">{row.month}</div>
                    <div className="history-cell history-cell--status">
                        {row.month ? (
                            <span className={`history-status-pill${row.status ? '' : ' history-status-pill--empty'}`}>
                                {row.status || ''}
                            </span>
                        ) : null}
                    </div>
                    <div className="history-cell history-cell--num">
                        {formatBillAmount(row.units)}
                    </div>
                    <div className="history-cell history-cell--num">
                        {formatBillAmount(row.bill)}
                    </div>
                    <div className="history-cell history-cell--num history-cell--payment">
                        {formatBillAmount(row.payment)}
                    </div>
                </div>
            ))}
            {footer ? <div className="history-table-footer">{footer}</div> : null}
        </div>
    );
};

const BillMessagesBanner = ({ billData, subsidyQrUrl, onOpenPreview, className = '' }) => {
    if (billData.billMessagesLayoutOnly) {
        return (
            <div className={`bill-messages-banner ${className}`.trim()}>
                <div className="bill-messages-fpa bill-messages-fpa--empty" aria-hidden="true" />
                <div className="bill-messages-subsidy-block bill-messages-subsidy-block--empty">
                    <div className="bill-messages-subsidy-text" aria-hidden="true" />
                </div>
                <div className="bill-messages-subsidy-qr bill-messages-subsidy-qr--empty" aria-hidden="true" />
            </div>
        );
    }

    const totalCharges = formatBillAmount(billData.totalElectricityCharges || '0') || '0';
    const subsidyAmount =
        formatBillAmount(String(billData.subsidies || '0').replace('-', '')) || '0';

    return (
        <div className={`bill-messages-banner ${className}`.trim()}>
            {billData.fpaMessageUrdu ? (
                <div className="bill-messages-fpa" lang="ur" dir="rtl">
                    {billData.fpaMessageUrdu}
                </div>
            ) : null}
            <div className="bill-messages-subsidy-block">
                <div className="bill-messages-subsidy-text">
                    {billData.noticeUrdu || (
                        <>
                            آپ کے موجودہ بجلی کے بل <strong>{totalCharges}</strong> روپے میں حکومتِ پاکستان کی جانب سے{' '}
                            <strong>{subsidyAmount}</strong> روپے کی سبسڈی شامل ہے۔ اس سبسڈی کی مجوزہ اہلیت کی تصدیق کے لیے
                            QR کوڈ اسکین کریں اور رجسٹریشن کروائیں۔ بصورتِ دیگر آپ کی سبسڈی معطل ہو سکتی ہے۔
                        </>
                    )}
                </div>
            </div>
            {subsidyQrUrl ? (
                <button
                    type="button"
                    className="bill-messages-subsidy-qr bill-messages-subsidy-qr--clickable"
                    onClick={() => onOpenPreview?.(subsidyQrUrl, 'qr')}
                    aria-label="Enlarge subsidy QR"
                >
                    <img src={subsidyQrUrl} alt="Subsidy QR" />
                </button>
            ) : null}
        </div>
    );
};

const MeterSnapPanel = ({ billData, onOpenPreview }) => {
    const metaLine1 =
        billData.meterSnapMetaLine1 ||
        [billData.meterSnapCapturedBy, billData.meterSnapCapturedAt, billData.meterSnapReference]
            .filter(Boolean)
            .join(' ');

    const versionLabel = billData.meterSnapVersion
        ? `Ver ${String(billData.meterSnapVersion).replace(/^v/i, '')}`
        : '';

    const metaLine2 =
        billData.meterSnapMetaLine2 ||
        [billData.meterSnapGps, versionLabel].filter(Boolean).join(' ');

    const amountPaidNote = billData.amountPaidNote || '';
    const [brokenSrcs, setBrokenSrcs] = useState(() => new Set());
    const [resolvedImages, setResolvedImages] = useState([]);

    useEffect(() => {
        let cancelled = false;
        const raw = (
            Array.isArray(billData.meterImageUrls)
                ? billData.meterImageUrls.filter(Boolean)
                : billData.meterImageUrl
                    ? [billData.meterImageUrl]
                    : []
        );
        const pdfs = Array.isArray(billData.meterImagePdfs)
            ? billData.meterImagePdfs.filter(Boolean)
            : [];
        const hasPdf = pdfs.length > 0 || raw.some((src) => isPdf(src));

        const apply = (urls) => {
            if (!cancelled) setResolvedImages(urls.slice(0, 4));
        };

        if (!hasPdf) {
            apply(raw);
            return undefined;
        }

        (async () => {
            const out = [];
            for (const src of [...raw, ...pdfs]) {
                if (out.length >= 4) break;
                if (isPdf(src)) {
                    const pages = await pdfToImages(src);
                    out.push(...pages);
                } else {
                    out.push(src);
                }
            }
            apply(out);
        })();

        return () => {
            cancelled = true;
        };
    }, [billData.meterImageUrls, billData.meterImageUrl, billData.meterImagePdfs]);

    const meterImages = resolvedImages
        .filter((src) => !brokenSrcs.has(src))
        .slice(0, 4);
    const imageCount = meterImages.length;
    const gridClass =
        imageCount === 1
            ? 'meter-snaps-grid meter-snaps-grid--1'
            : imageCount === 2
                ? 'meter-snaps-grid meter-snaps-grid--2'
                : imageCount === 3
                    ? 'meter-snaps-grid meter-snaps-grid--3'
                    : 'meter-snaps-grid meter-snaps-grid--4';

    return (
        <div className="meter-snaps-card meter-snaps-card--verify">
            <div className="meter-snap-photo-stage">
                {metaLine1 || metaLine2 ? (
                    <div className="meter-snap-meta meter-snap-meta--overlay">
                        {metaLine1 ? <div className="meter-snap-meta__line">{metaLine1}</div> : null}
                        {metaLine2 ? <div className="meter-snap-meta__line">{metaLine2}</div> : null}
                    </div>
                ) : null}
                {imageCount ? (
                    <div className={gridClass}>
                        {meterImages.map((src, index) => (
                            <button
                                type="button"
                                key={`meter-snap-${index}`}
                                className="meter-snap-cell meter-snap-cell--clickable"
                                onClick={() => onOpenPreview?.(src, 'image')}
                                aria-label={`Enlarge meter reading ${index + 1}`}
                            >
                                <img
                                    src={src}
                                    alt={`Meter reading ${index + 1}`}
                                    className="meter-snap-photo"
                                    onError={() => {
                                        setBrokenSrcs((prev) => {
                                            if (prev.has(src)) return prev;
                                            const next = new Set(prev);
                                            next.add(src);
                                            return next;
                                        });
                                    }}
                                    style={{
                                        objectFit: billData.meterImageFit || 'contain',
                                        objectPosition:
                                            billData.meterImagePosition || 'center',
                                    }}
                                />
                            </button>
                        ))}
                    </div>
                ) : (
                    <div className="meter-snap-placeholder meter-snap-placeholder--empty" aria-hidden="true" />
                )}
                {billData.paidOverlay ? (
                    <div
                        className="bill-payment-logo"
                        style={{ backgroundImage: `url('${billData.paidOverlay}')` }}
                    />
                ) : billData.showPaidStamp ? (
                    <div className="paid-stamp-badge" aria-hidden="true">
                        <span className="paid-stamp-badge__pre">BILL PAID</span>
                        <span className="paid-stamp-badge__main">PAID</span>
                        <span className="paid-stamp-badge__stars">★★★</span>
                        <span className="paid-stamp-badge__sub">VERIFIED BY PITC</span>
                    </div>
                ) : null}
            </div>
            {amountPaidNote ? <div className="meter-snap-paid-footer">{amountPaidNote}</div> : null}
        </div>
    );
};

const SlipMatrixGrid = ({ billData }) => {
    const payableAmount = formatBillAmount(
        billData.payableWithinDueDate ??
            billData.payableAmount ??
            billData.cp52Amount ??
            billData.grandTotal ??
            ''
    );
    const tillDate = billData.surchargeTillDate || billData.dueDate || '';
    const afterDate = billData.surchargeAfterDate || billData.dueDate || '';

    return (
        <div className="slip-matrix-grid">
            <div className="slip-matrix-col slip-matrix-col--month">
                <div className="slip-matrix-col__label">
                    BILL MONTH <span className="ur-lbl">مہینہ بل</span>
                </div>
                <div className="slip-matrix-col__value">{billData.billMonth}</div>
            </div>

            <div className="slip-matrix-col slip-matrix-col--due">
                <div className="slip-matrix-col__label">
                    DUE DATE <span className="ur-lbl">مقررہ تاریخ</span>
                </div>
                <div className="slip-matrix-col__value">{billData.dueDate}</div>
            </div>

            <div className="slip-matrix-payable">
                <div className="slip-matrix-payable__within">
                    <div className="slip-matrix-payable__within-label">
                        PAYABLE WITHIN DUE DATE{' '}
                        <span className="ur-lbl">مقررہ تاریخ تک واجب الادا</span>
                    </div>
                    <div className="slip-matrix-payable__within-amount">{payableAmount}</div>
                </div>
                <div className="slip-matrix-payable__after">
                    <div className="slip-matrix-payable__after-label">
                        PAYABLE AFTER DUE DATE{' '}
                        <span className="ur-lbl">مقررہ تاریخ کے بعد واجب الادا</span>
                    </div>
                    <div className="slip-matrix-payable__after-cell">
                        <span>Till {tillDate}</span>
                        <strong>
                            {formatBillAmount(billData.payableAfterDueDateTill)}
                        </strong>
                    </div>
                    <div className="slip-matrix-payable__after-cell">
                        <span>After {afterDate}</span>
                        <strong>
                            {formatBillAmount(billData.payableAfterDueDateAfter)}
                        </strong>
                    </div>
                </div>
            </div>
        </div>
    );
};

const MrcElectricityConsumerBill = ({ billData = {}, className = '', id }) => {
    const safeBillData = billData || {};
    const [headerQrUrl, setHeaderQrUrl] = useState('');
    const [sidebarQrUrl, setSidebarQrUrl] = useState('');
    const [subsidyQrUrl, setSubsidyQrUrl] = useState('');
    const [mediaPreview, setMediaPreview] = useState(null);
    const barcodeRef = useRef(null);

    const openMediaPreview = (src, kind = 'image') => {
        const url = String(src || '').trim();
        if (!url) return;
        setMediaPreview({ src: url, kind });
    };

    const closeMediaPreview = () => setMediaPreview(null);

    const qrValue = String(safeBillData.raastQr || '').trim();

    const subsidyQrValue = String(
        safeBillData.raastQr ||
            safeBillData.subsidyQrUrl ||
            safeBillData.subsidyEligibilityUrl ||
            ''
    ).trim();

    const payableForQr = Number(
        String(
            safeBillData.payableWithinDueDate ??
                safeBillData.payableAmount ??
                safeBillData.grandTotal ??
                ''
        ).replace(/,/g, '').trim()
    );
    const hideQrContent =
        Boolean(safeBillData.hideQrContent) ||
        (Number.isFinite(payableForQr) && payableForQr < 0);
    const hideHeaderUrduText = Boolean(safeBillData.hideHeaderUrduText);

    useEffect(() => {
        if (!mediaPreview) return undefined;
        const onKeyDown = (event) => {
            if (event.key === 'Escape') closeMediaPreview();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [mediaPreview]);

    useEffect(() => {
        if (hideQrContent) {
            setHeaderQrUrl('');
            setSidebarQrUrl('');
            setSubsidyQrUrl('');
            return undefined;
        }

        // DN / MRC payment QR API may already return a rendered image data URL.
        if (String(qrValue || '').startsWith('data:image')) {
            setHeaderQrUrl(qrValue);
            setSidebarQrUrl(qrValue);
            setSubsidyQrUrl(
                String(subsidyQrValue || '').startsWith('data:image')
                    ? subsidyQrValue
                    : qrValue
            );
            return undefined;
        }

        const makeQr = (value, setter, width = 120) => {
            if (!value || value === '-') {
                setter('');
                return Promise.resolve();
            }
            return QRCode.toDataURL(String(value), {
                errorCorrectionLevel: 'M',
                margin: 0,
                width,
            })
                .then(setter)
                .catch(() => setter(''));
        };

        makeQr(qrValue, setHeaderQrUrl, 180);
        makeQr(qrValue, setSidebarQrUrl, 90);
        makeQr(subsidyQrValue || qrValue, setSubsidyQrUrl, 70);
        return undefined;
    }, [qrValue, subsidyQrValue, hideQrContent]);

    useEffect(() => {
        if (!barcodeRef.current) return;
        const barcodeValue = safeBillData.barcodeRaw || safeBillData.barcodeValue;
        if (!barcodeValue) {
            barcodeRef.current.innerHTML = '';
            return;
        }
        try {
            JsBarcode(barcodeRef.current, String(barcodeValue).replace(/\s/g, ''), {
                width: 1.1,
                height: 38,
                margin: 2,
                displayValue: false,
            });
        } catch {
            barcodeRef.current.innerHTML = '';
        }
    }, [safeBillData.barcodeRaw, safeBillData.barcodeValue]);

    if (!billData) return null;

    const logo = safeBillData.discoCode === '12' ? gepcoBillHeaderLogo : getLogo(safeBillData.discoCode);
    const historyRows = safeBillData.billHistory || [];
    const leftHistory = historyRows.slice(0, Math.ceil(historyRows.length / 2));
    const rightHistory = historyRows.slice(Math.ceil(historyRows.length / 2));
    const addressLines = String(safeBillData.address || '')
        .split(/\n|,\s*/)
        .filter(Boolean);
    const nameLine = safeBillData.consumerName || '';
    const showProRata = /pro-rata/i.test(String(safeBillData.meterNote || safeBillData.status || ''));
    // PAYABLE AMOUNT = getCP52 AMOUNT (mapped into payableWithinDueDate)
    const payableAmountDisplay = formatBillAmount(
        safeBillData.payableWithinDueDate ??
            safeBillData.payableAmount ??
            safeBillData.cp52Amount ??
            safeBillData.grandTotal ??
            ''
    );

    const billBrandStyle = {
        '--bill-logo-color': safeBillData.logoColor || '#1b6b38',
        '--bill-logo-color-light': safeBillData.logoColorLight || '#c8e6d0',
    };

    return (
        <div
            className={`mrc-electricity-consumer-bill gbn-print-root ${className}`}
            id={id}
            style={billBrandStyle}
        >
            <div className="a4-container maincontent">
                <header className="bill-header">
                    <div className="header-left">
                        <img className="header-brand-logo" src={logo} alt={safeBillData.companyShortName || 'DISCO'} />
                        <div className="ellipsis-protect header-gst">
                            {safeBillData.gstLabel ||
                                `${safeBillData.companyShortName ? `${safeBillData.companyShortName} ` : ''}GST NO: ${safeBillData.gstNo}`}
                        </div>
                        <div className="ellipsis-protect header-website">{safeBillData.website}</div>
                        <div className="text-danger ellipsis-protect header-corruption">Say No To Corruption</div>
                    </div>
                    <div className="header-center">
                        <h1 className="brand-logo ellipsis-protect">{safeBillData.companyTitle}</h1>
                        <h2 className="ellipsis-protect header-tagline">YOUR BETTER SERVICE – OUR PRIDE</h2>
                        <h3 className="brand-blue ellipsis-protect">ELECTRICITY CONSUMER BILL</h3>
                    </div>
                    <div className="header-right">
                        {hideQrContent ? (
                            <div className="header-qr-urdu-row header-qr-urdu-row--empty" aria-hidden="true" />
                        ) : (
                            <div className="header-qr-urdu-row">
                                {headerQrUrl ? (
                                    <button
                                        type="button"
                                        className="header-qr-host header-qr-host--clickable"
                                        onClick={() => openMediaPreview(headerQrUrl, 'qr')}
                                        aria-label="Enlarge payment QR"
                                    >
                                        <img src={headerQrUrl} alt="Bill QR Code" />
                                    </button>
                                ) : null}
                                <div className="header-urdu-text">
                                    <div className="ur-lbl header-payment-urdu">
                                        {hideHeaderUrduText
                                            ? ''
                                            : safeBillData.headerQrUrdu ||
                                              'راست اپنائیں۔ QR کوڈ سے ادائیگی کریں۔ انعام پائیں۔'}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </header>

                <main className="main-dashboard-grid">
                    <div className="bill-section bill-section--consumer component-card">
                        <div className="card-header">☰ CONSUMER DETAIL</div>
                        <div className="grid-4col">
                            <GridColCell>
                                <FieldBlock en="REFERENCE NO" urdu="حوالہ نمبر" value={safeBillData.referenceNo} />
                                <FieldBlock en="CONSUMER ID" urdu="کنزیومر نمبر" value={safeBillData.consumerId} />
                                <LabelRow urdu="نام و پتہ" urduOnly />
                                <ValSpace className="val-space--address">
                                    {nameLine ? <span>{nameLine}</span> : null}
                                    {addressLines.map((line) => (
                                        <span key={line}>{line}</span>
                                    ))}
                                </ValSpace>
                            </GridColCell>
                            <GridColCell divider>
                                <FieldBlock en="TRANSFORMER" urdu="ٹرانسفارمر" value={safeBillData.transformer} />
                                <FieldBlock en="FEEDER" urdu="فیڈر" value={safeBillData.feeder} />
                                <FieldBlock en="SUB DIVISION" urdu="سب ڈویژن" value={safeBillData.subDivision} />
                            </GridColCell>
                            <GridColCell divider>
                                <FieldBlock en="CATEGORY" urdu="قسم" value={safeBillData.category} />
                                <FieldBlock en="STATUS" urdu="حالت میٹر" value={safeBillData.status} />
                                <FieldBlock en="TARIFF CATEGORY" urdu="ٹیرف قسم" value={safeBillData.tariffCategory} />
                            </GridColCell>
                            <GridColCell divider>
                                <FieldBlock en="SAN LOAD" urdu="لوڈ" value={safeBillData.sanctionedLoad} />
                                <FieldBlock en="CURR MDI" urdu="ایم ڈی آئی" value={safeBillData.mdi} />
                                <FieldBlock en="TARIFF" urdu="ٹیرف" value={safeBillData.tariff} valueClassName="val-space--tariff" />
                            </GridColCell>
                        </div>
                    </div>

                    <div className="bill-section bill-section--meter component-card">
                        <div className="card-header">METER INFO</div>
                        {safeBillData.meterInfoTable?.typeRows?.length ? (
                            <>
                                <table className="mrc-meter-info-table">
                                    <thead>
                                        <tr>
                                            <th>METER</th>
                                            <th>TYPE</th>
                                            <th>PREVIOUS</th>
                                            <th>PRESENT</th>
                                            <th>MF</th>
                                            <th>UNITS</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {safeBillData.meterInfoTable.typeRows.map(
                                            (typeRow, typeIndex) => {
                                                const maxStack = Math.max(
                                                    1,
                                                    ...safeBillData.meterInfoTable.typeRows.map(
                                                        (r) =>
                                                            Math.max(
                                                                r.previous?.length || 0,
                                                                r.present?.length || 0,
                                                                r.mf?.length || 0,
                                                                r.units?.length || 0,
                                                                1
                                                            )
                                                    )
                                                );
                                                return (
                                                    <tr key={`${typeRow.type}-${typeIndex}`}>
                                                        {typeIndex === 0 ? (
                                                            <td
                                                                className="mrc-meter-info-table__meter"
                                                                rowSpan={
                                                                    safeBillData.meterInfoTable
                                                                        .typeRows.length
                                                                }
                                                            >
                                                                {Array.from({
                                                                    length: maxStack,
                                                                }).map((_, i) => (
                                                                    <div
                                                                        key={`mno-${i}`}
                                                                        className="mrc-meter-info-table__stack-val"
                                                                    >
                                                                        {(safeBillData
                                                                            .meterInfoTable
                                                                            .meterNos &&
                                                                            safeBillData
                                                                                .meterInfoTable
                                                                                .meterNos[i]) ||
                                                                            safeBillData
                                                                                .meterInfoTable
                                                                                .meterNo ||
                                                                            safeBillData.meterNo ||
                                                                            '—'}
                                                                    </div>
                                                                ))}
                                                            </td>
                                                        ) : null}
                                                        <td className="mrc-meter-info-table__type">
                                                            {typeRow.type}
                                                        </td>
                                                        <td>
                                                            {(typeRow.previous || ['—']).map(
                                                                (val, i) => (
                                                                    <div
                                                                        key={`prev-${i}`}
                                                                        className="mrc-meter-info-table__stack-val"
                                                                    >
                                                                        {val}
                                                                    </div>
                                                                )
                                                            )}
                                                        </td>
                                                        <td>
                                                            {(typeRow.present || ['—']).map(
                                                                (val, i) => (
                                                                    <div
                                                                        key={`pres-${i}`}
                                                                        className="mrc-meter-info-table__stack-val"
                                                                    >
                                                                        {val}
                                                                    </div>
                                                                )
                                                            )}
                                                        </td>
                                                        <td>
                                                            {(typeRow.mf || ['—']).map(
                                                                (val, i) => (
                                                                    <div
                                                                        key={`mf-${i}`}
                                                                        className="mrc-meter-info-table__stack-val"
                                                                    >
                                                                        {val}
                                                                    </div>
                                                                )
                                                            )}
                                                        </td>
                                                        <td className="mrc-meter-info-table__units">
                                                            {(typeRow.units ||
                                                                (typeRow.unitsDisplay
                                                                    ? [typeRow.unitsDisplay]
                                                                    : ['—'])
                                                            ).map((val, i) => (
                                                                <div
                                                                    key={`units-${i}`}
                                                                    className="mrc-meter-info-table__stack-val mrc-meter-info-table__units-line"
                                                                >
                                                                    {val}
                                                                </div>
                                                            ))}
                                                        </td>
                                                    </tr>
                                                );
                                            }
                                        )}
                                    </tbody>
                                </table>
                            </>
                        ) : (
                            <>
                                <div className="grid-4col">
                                    <GridColCell className="meter-reading-col">
                                        <FieldBlock en="MF" urdu="جزو ضرب" value={safeBillData.meterFactor} valueClassName="val-space--meter" />
                                    </GridColCell>
                                    <GridColCell divider className="meter-reading-col">
                                        <FieldBlock en="PREVIOUS READING" urdu="سابقہ ریڈنگ" value={safeBillData.previousReading} valueClassName="val-space--meter" />
                                    </GridColCell>
                                    <GridColCell divider className="meter-reading-col">
                                        <FieldBlock en="PRESENT READING" urdu="موجودہ ریڈنگ" value={safeBillData.presentReading} valueClassName="val-space--meter" />
                                    </GridColCell>
                                    <GridColCell divider className="meter-reading-col">
                                        <FieldBlock en="UNITS" urdu="یونٹ" value={safeBillData.units} valueClassName="val-space--meter" />
                                    </GridColCell>
                                </div>
                                <div className="meter-info-footer">
                                    <span>
                                        <strong>METER NO:</strong> {safeBillData.meterNo}
                                    </span>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="bill-section bill-section--charges component-card charges-breakdown-card">
                        <div className="card-header">
                            BILL CHARGES BREAKDOWN <span className="charges-breakdown-rs">(RS.)</span>
                        </div>
                        <ChargesBreakdown billData={safeBillData} />
                    </div>

                    <div className="bill-section bill-section--sidebar">
                        <div className="right-panel-box">
                            <div className="right-section-cell">
                                <div className="right-section-label">
                                    <span>BILL MONTH</span>
                                    <span className="ur-lbl">مہینہ بل</span>
                                </div>
                                <div className="right-main-val right-main-val--month">{safeBillData.billMonth}</div>
                            </div>
                            <div className="right-grid-2col">
                                <div className="right-grid-cell">
                                    <span className="right-grid-en">
                                        READING DATE
                                        {showProRata ? <span className="pro-rata-tag"> (Pro-Rata)</span> : null}
                                    </span>
                                    <span className="ur-lbl">تاریخ ریڈنگ</span>
                                    <span className="right-grid-val">{safeBillData.readingDate}</span>
                                </div>
                                <div className="right-grid-cell">
                                    <span className="right-grid-en">ISSUE DATE</span>
                                    <span className="ur-lbl">تاریخ اجراء</span>
                                    <span className="right-grid-val">{safeBillData.issueDate}</span>
                                </div>
                            </div>
                            <div className="right-section-cell">
                                <div className="right-section-label">
                                    <span className="due-date-label">⚠ DUE DATE</span>
                                    <span className="ur-lbl">مقررہ تاریخ</span>
                                </div>
                                <div className="right-main-val right-main-val--due">{safeBillData.dueDate}</div>
                            </div>
                        </div>

                        <div className="payable-card">
                            <div className="payable-card__labels">
                                <span className="payable-card__en">PAYABLE WITHIN DUE DATE</span>
                                <span className="ur-lbl">مقررہ تاریخ تک واجب الادا</span>
                            </div>
                            <div className="payable-card__amount">{payableAmountDisplay}</div>
                        </div>

                        <div className="surcharge-panel">
                            <div className="surcharge-panel__grid">
                                <div className="surcharge-panel__label-col">
                                    <div>
                                        <div className="surcharge-panel__title">L.P</div>
                                        <div className="surcharge-panel__title">SURCHARGE</div>
                                    </div>
                                    <div className="surcharge-panel__divider">
                                        <div className="surcharge-panel__title">PAYABLE</div>
                                        <div className="surcharge-panel__title">AFTER DUE</div>
                                        <div className="surcharge-panel__title">DATE</div>
                                        <div className="ur-lbl surcharge-panel__urdu">کے بعد واجب الادا</div>
                                    </div>
                                </div>
                                <div className="surcharge-panel__amount-col">
                                    <div className="surcharge-panel__lp">
                                        {formatBillAmount(safeBillData.lpSurcharge)}
                                    </div>
                                    <div className="surcharge-panel__divider">
                                        <div className="surcharge-panel__date">
                                            Till {safeBillData.surchargeTillDate || safeBillData.dueDate}
                                        </div>
                                        <div className="surcharge-panel__payable">
                                            {formatBillAmount(
                                                safeBillData.payableAfterDueDateTill
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="surcharge-panel__amount-col">
                                    <div className="surcharge-panel__lp">
                                        {formatBillAmount(
                                            safeBillData.lpSurchargeAfter ||
                                                safeBillData.lpSurcharge
                                        )}
                                    </div>
                                    <div className="surcharge-panel__divider">
                                        <div className="surcharge-panel__date">
                                            After {safeBillData.surchargeAfterDate || safeBillData.dueDate}
                                        </div>
                                        <div className="surcharge-panel__payable">
                                            {formatBillAmount(
                                                safeBillData.payableAfterDueDateAfter
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <MeterSnapPanel billData={safeBillData} onOpenPreview={openMediaPreview} />
                    </div>

                    <BillMessagesBanner
                        billData={safeBillData}
                        subsidyQrUrl={subsidyQrUrl}
                        onOpenPreview={openMediaPreview}
                        className="bill-section bill-section--messages"
                    />

                    <div className="bill-section bill-section--history history-card">
                        <div className="history-card-header">
                            <span>BILL HISTORY</span>
                            <span>
                                {safeBillData.companyShortName || 'DISCO'}
                                <br />
                                Consumer
                            </span>
                        </div>
                        <div className="history-data-wrapper">
                            <HistoryBlockGrid
                                rows={leftHistory}
                                footer={
                                    <>
                                        <span className="history-footer-en">Deferred Amount</span>
                                        <span className="history-footer-ur ur-lbl">مؤخر شدہ رقم</span>
                                    </>
                                }
                            />
                            <HistoryBlockGrid
                                rows={rightHistory}
                                className="history-center-divider"
                                footer={
                                    <>
                                        <span className="history-footer-en">Outstanding Install.</span>
                                        <span className="history-footer-ur ur-lbl">بقایا اقساط</span>
                                    </>
                                }
                            />
                        </div>
                    </div>
                </main>

                <footer className="perforated-slip">
                    <div className="slip-branding-grid">
                        <div className="slip-brand-col">
                            <img className="disco-logo-slip" src={logo} alt="" />
                            <div className="bank-stamp-container">
                                <div className="bank-stamp-circle">BANK STAMP</div>
                            </div>
                        </div>
                        <div className="slip-title-area">
                            <h4 className="brand-logo">{safeBillData.companyTitle}</h4>
                            <h4 className="brand-blue">ELECTRICITY CONSUMER BILL</h4>
                            <div className="slip-subtitle">YOUR BETTER SERVICE - OUR PRIDE</div>
                            <div className="slip-urdu-line">
                                وزیر اعظم انرجی ایفیشینٹ فین ریپلیسمنٹ پروگرام کا آغاز
                            </div>
                            <div className="slip-url">https://efan.gov.pk/</div>
                        </div>
                        <div className="slip-barcode-col">
                            <div className="barcode-placeholder" aria-label="Payment barcode">
                                <svg ref={barcodeRef} />
                            </div>
                            {safeBillData.barcodeCaption ? (
                                <div className="barcode-caption">{safeBillData.barcodeCaption}</div>
                            ) : null}
                            <div className="slip-id-box">
                                <div className="slip-id-row">
                                    <span className="slip-id-en">CONSUMER ID</span>
                                    <span className="slip-id-ur ur-lbl">کنزیومر نمبر</span>
                                    <span className="slip-id-value">{safeBillData.consumerId}</span>
                                </div>
                                <div className="slip-id-row">
                                    <span className="slip-id-en">REFERENCE NO</span>
                                    <span className="slip-id-ur ur-lbl">حوالہ نمبر</span>
                                    <span className="slip-id-value">{safeBillData.referenceNo}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <SlipMatrixGrid billData={safeBillData} />
                </footer>
            </div>

            {mediaPreview ? (
                <div
                    className="mrc-media-preview-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-label={mediaPreview.kind === 'qr' ? 'Payment QR preview' : 'Meter image preview'}
                    onClick={closeMediaPreview}
                >
                    <div
                        className="mrc-media-preview-panel"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <img
                            src={mediaPreview.src}
                            alt={mediaPreview.kind === 'qr' ? 'Enlarged QR code' : 'Enlarged meter image'}
                            className={
                                mediaPreview.kind === 'qr'
                                    ? 'mrc-media-preview-image mrc-media-preview-image--qr'
                                    : 'mrc-media-preview-image mrc-media-preview-image--photo'
                            }
                        />
                        <p className="mrc-media-preview-hint">
                            {mediaPreview.kind === 'qr'
                                ? 'Scan payment QR, then tap outside to close'
                                : 'Tap outside to close'}
                        </p>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default MrcElectricityConsumerBill;
